import { memo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { trackEvent } from '../../utils/analytics'
import { handleShareURL } from '../../utils/mapHandlerUtils'

/**
 * ShareButton - Social sharing dropdown button
 * 
 * Features:
 * - Main share button toggle
 * - Dropdown with multiple platforms
 * - Platform-specific icons
 * - Generic share fallback
 * - Analytics tracking for all interactions
 * 
 * @idempotent - Self-contained state management
 * @atomic - Single purpose: social sharing
 */
const ShareButton = memo(function ShareButton({
  onShare,
  entityDetails,
  className = '',
  source = 'control_panel_search', // Context for analytics
  showLabel = false,
  label = 'Share'
}) {
  const { t } = useTranslation()
  const [showShareOptions, setShowShareOptions] = useState(false)

  const handleToggleDropdown = useCallback(() => {
    const newState = !showShareOptions
    setShowShareOptions(newState)
    if (newState) {
      trackEvent('Share', 'dropdown_open', source, null, { source, location: 'search_tab' })
    }
  }, [showShareOptions, source])

  const handleShareClick = useCallback((platform) => {
    trackEvent('Share', 'click', platform || 'native', null, {
      platform: platform || 'native_share',
      source,
      location: entityDetails ? 'entity_panel' : 'search_tab',
      entity_id: entityDetails?.id,
    })
    setShowShareOptions(false)

    if (entityDetails) {
      const baseUrl = import.meta.env.VITE_SITE_URL || window.location.origin
      const entityUrl = `${baseUrl}/entity/${entityDetails.id}`
      const entityName = entityDetails.name || 'this vendor'
      handleShareURL({
        url: entityUrl,
        title: entityName,
        text: t('entities.detail.shareText', { name: entityName }) || `Check out ${entityName} on TrckIt!`,
        platform,
        t,
        entityId: entityDetails.id,
      })
    } else if (onShare) {
      onShare(platform)
    }
  }, [onShare, entityDetails, source, t])

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={handleToggleDropdown}
        title={t('entities.detail.share')}
        className="w-full px-2 py-2.5 rounded-lg font-medium transition-all duration-150 flex flex-col items-center justify-center gap-1.5 bg-white hover:bg-gray-50 border border-gray-200 hover:border-gray-300 text-gray-500 hover:text-gray-700 shadow-sm hover:shadow-md"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 flex-shrink-0">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
        </svg>
        {showLabel && <span className="text-[10px] font-semibold leading-none tracking-wide">{label}</span>}
      </button>

      {/* Social Share Options Dropdown */}
      {showShareOptions && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-2">
          <button
            onClick={() => handleShareClick()}
            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 rounded flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
            </svg>
            {t('entities.detail.shareGeneric') || 'Share...'}
          </button>
          
          <button
            onClick={() => handleShareClick('twitter')}
            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 rounded flex items-center gap-2"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            X (Twitter)
          </button>
          
          <button
            onClick={() => handleShareClick('facebook')}
            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 rounded flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Facebook
          </button>
          
          <button
            onClick={() => handleShareClick('linkedin')}
            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 rounded flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
            LinkedIn
          </button>
        </div>
      )}
    </div>
  )
})

export default ShareButton
